import React, { useState, useRef, useEffect, useCallback } from "react";
import { useTranslation } from "react-i18next";
import "./LanguageSelector.css";

const LANGUAGES = [
  { code: "en", label: "English", flag: "🇺🇸" },
  { code: "ja", label: "日本語", flag: "🇯🇵" },
];

const LanguageSelector = ({ className = "" }) => {
  const { i18n, t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(-1);
  const dropdownRef = useRef(null);
  const buttonRef = useRef(null);
  const optionRefs = useRef([]);

  // Derive current language safely (fallback to "en")
  const currentLangCode =
    i18n.resolvedLanguage?.startsWith("ja") || i18n.language?.startsWith("ja")
      ? "ja"
      : "en";

  const currentLang =
    LANGUAGES.find((lang) => lang.code === currentLangCode) || LANGUAGES[0];

  const toggleDropdown = () => {
    setIsOpen((prev) => !prev);
  };

  const handleLanguageChange = useCallback(
    async (code) => {
      try {
        if (i18n.changeLanguage) {
          await i18n.changeLanguage(code);
        }
      } catch (error) {
        console.error("Failed to change language:", error);
      } finally {
        setIsOpen(false);
        buttonRef.current?.focus();
      }
    },
    [i18n],
  );

  // Close dropdown on click outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target)
      ) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isOpen]);

  // Sync activeIndex with current selected language when menu opens
  useEffect(() => {
    if (!isOpen) {
      setActiveIndex(-1);
      return;
    }
    const idx = LANGUAGES.findIndex((lang) => lang.code === currentLangCode);
    setActiveIndex(idx >= 0 ? idx : 0);
  }, [isOpen, currentLangCode]);

  // Handle focusing the active option
  useEffect(() => {
    if (isOpen && activeIndex >= 0) {
      optionRefs.current[activeIndex]?.focus();
    }
  }, [activeIndex, isOpen]);

  // Handle keyboard events
  const handleKeyDown = (e) => {
    if (e.key === "Escape") {
      setIsOpen(false);
      buttonRef.current?.focus();
      return;
    }

    if (!isOpen) return;

    const len = LANGUAGES.length;
    const clamp = (n) => ((n % len) + len) % len;

    if (e.key === "ArrowDown") {
      e.preventDefault();
      setActiveIndex((i) => clamp(i + 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setActiveIndex((i) => clamp(i - 1));
    } else if (e.key === "Home") {
      e.preventDefault();
      setActiveIndex(0);
    } else if (e.key === "End") {
      e.preventDefault();
      setActiveIndex(len - 1);
    } else if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      const idx = activeIndex >= 0 ? activeIndex : 0;
      handleLanguageChange(LANGUAGES[idx].code);
    }
  };

  return (
    <div
      className={`language-selector-wrapper ${className}`}
      ref={dropdownRef}
      onKeyDown={handleKeyDown}
    >
      <button
        ref={buttonRef}
        type="button"
        className="btn language-selector-btn"
        onClick={toggleDropdown}
        aria-expanded={isOpen}
        aria-haspopup="listbox"
        aria-label={t("language.selectLanguage", "Select language")}
        data-testid="language-selector-btn"
      >
        <span className="lang-icon">
          <i className="bi bi-translate"></i>
        </span>
        <span className="lang-label">{currentLang.label}</span>
        <i className="bi bi-chevron-down lang-chevron"></i>
      </button>

      {isOpen && (
        <ul
          className="language-selector-menu"
          role="listbox"
          aria-label={t("language.selectLanguage", "Select language")}
          data-testid="language-selector-dropdown"
        >
          {LANGUAGES.map((lang, index) => {
            const isSelected = lang.code === currentLangCode;
            return (
              <li key={lang.code} role="presentation">
                <button
                  ref={(el) => (optionRefs.current[index] = el)}
                  type="button"
                  role="option"
                  aria-selected={isSelected}
                  tabIndex={activeIndex === index ? 0 : -1}
                  className={`language-option-item ${
                    isSelected ? "active" : ""
                  }`}
                  onClick={() => handleLanguageChange(lang.code)}
                  data-testid={`language-option-${lang.code}`}
                >
                  <span className="language-option-content">
                    <span className="language-flag" aria-hidden="true">
                      {lang.flag}
                    </span>
                    <span>{lang.label}</span>
                  </span>
                  {isSelected && (
                    <i
                      className="bi bi-check2 language-check-icon"
                      aria-hidden="true"
                    ></i>
                  )}
                </button>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
};

export default LanguageSelector;
